import React, { useEffect, useState } from 'react';
import { ChevronRight, Plus, Search } from 'lucide-react';
import { FilterSearchInput } from '../../../components/FormComponents';

// The rail only shows its filter once the schema set is big enough to need
// one — below this it must not render (doc-105 §3.2).
const FILTER_THRESHOLD = 8;

function RailRow({ label, active, dot, onSelect }) {
  const name = label || 'Untitled schema';
  return (
    <button type="button" className={`pb-rail-item ${active ? 'active' : ''}`} onClick={onSelect} title={name}>
      <span className="label">{name}</span>
      {dot && <span className="dot" />}
    </button>
  );
}

// The Data Schemas master rail: live schemas, then a collapsed "Archived"
// section. Pure navigation (doc-108) — archive/restore/delete live in the
// editor's Settings tab, and the output count lives in the header meta line,
// so rows are just name + dirty dot, matching the playbooks rail.
export default function SchemaRail({
  items,            // live schemas + unsaved drafts: { id, name, isNew }
  archivedItems,    // archived schemas: { id, name }
  selectedId,
  isDirty,          // (id) => bool
  onSelect,
  onAddNew,
}) {
  const selectedIsArchived = archivedItems.some((i) => i.id === selectedId);
  const [archivedOpen, setArchivedOpen] = useState(selectedIsArchived);
  // Selecting an archived schema (e.g. right after archiving it) keeps its
  // row visible instead of stranding the selection in a collapsed section.
  useEffect(() => {
    if (selectedIsArchived) setArchivedOpen(true);
  }, [selectedIsArchived]);

  const [query, setQuery] = useState('');
  const filterable = items.length + archivedItems.length >= FILTER_THRESHOLD;
  const q = query.trim().toLowerCase();
  const matches = (item) => !q || (item.name || 'Untitled schema').toLowerCase().includes(q);
  const visibleItems = filterable ? items.filter(matches) : items;
  const visibleArchived = filterable ? archivedItems.filter(matches) : archivedItems;

  return (
    <div className="stack-md schema-rail">
      <div>
        <button type="button" className="btn ghost sm" onClick={onAddNew}>
          <Plus className="w-3.5 h-3.5" />
          New schema
        </button>
      </div>
      {filterable && (
        <FilterSearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter schemas…"
          icon={Search}
          aria-label="Filter schemas by name"
        />
      )}
      {/* schema-rail-list: stacked (below 900px) this list is height-capped and
          scrolls internally so it cannot push the editor below the fold. */}
      <div className="stack-sm schema-rail-list">
        {visibleItems.map((item) => (
          <RailRow
            key={item.id}
            label={item.name}
            active={selectedId === item.id}
            dot={isDirty(item.id)}
            onSelect={() => onSelect(item.id)}
          />
        ))}
      </div>
      {visibleArchived.length > 0 && (
        <div className="stack-sm">
          <button
            type="button"
            className="pb-rail-item pb-rail-section"
            aria-expanded={archivedOpen}
            onClick={() => setArchivedOpen((v) => !v)}
          >
            <ChevronRight size={13} style={{ flexShrink: 0, transition: 'transform 0.12s ease', transform: archivedOpen ? 'rotate(90deg)' : 'none' }} />
            <span className="label">Archived ({visibleArchived.length})</span>
          </button>
          {archivedOpen && visibleArchived.map((item) => (
            <RailRow
              key={item.id}
              label={item.name}
              active={selectedId === item.id}
              dot={isDirty(item.id)}
              onSelect={() => onSelect(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
